import {Box, Tab, Tabs} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import {t} from 'i18next';
import {AIReviewJobType} from '../../../../../api/types/aiReview.type';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ProtocolCaseContext from '../casecontextv2/ProtocolCaseContext';
import IssueCodesTab from '../issue-codes-tab/IssueCodesTab';
import RelevanceCriteria from '../relevance-criteria/RelevanceCriteria';
import {JobDetailsArtifacts} from './jobDetailsArtifacts';
import './JobDetailsArtifactsTabs.scss';

const ISSUE_CODING: AIReviewJobType = 'ISSUECODE';

export interface JobDetailsArtifactsTabsProps {
  jobType?: AIReviewJobType;
  artifacts: JobDetailsArtifacts | null;
  isLoading?: boolean;
}

interface TabData {
  id: string;
  label: string;
  content: React.ReactNode;
}

const JobDetailsArtifactsTabs = ({
  jobType,
  artifacts,
  isLoading = false,
}: JobDetailsArtifactsTabsProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const isIssueCoding = jobType === ISSUE_CODING;

  const tabData = useMemo<TabData[]>(() => {
    if (!artifacts) {
      return [];
    }

    return [
      {
        id: 'case-context',
        label: t('ai.review.jobDetailsTab.caseContext'),
        content: (
          <ProtocolCaseContext mode="view" caseContext={artifacts.caseContext} />
        ),
      },
      {
        id: isIssueCoding ? 'issue-codes' : 'relevance-criteria',
        label: isIssueCoding
          ? t('ai.review.jobDetailsTab.issueCodes')
          : t('ai.review.jobDetailsTab.relevanceCriteria'),
        content: isIssueCoding ? (
          <IssueCodesTab mode="view" data={artifacts.issueCoding} />
        ) : (
          <RelevanceCriteria
            mode="view"
            criteria={artifacts.relevanceCriteria}
          />
        ),
      },
    ];
  }, [artifacts, isIssueCoding]);

  useEffect(() => {
    setActiveTab(0);
  }, [jobType]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const a11yProps = (index: number) => ({
    id: `job-details-artifacts-tab-${index}`,
    'aria-controls': `job-details-artifacts-tabpanel-${index}`,
  });

  if (isLoading) {
    return (
      <Box
        className="job-details-artifacts-tabs"
        data-testid="job-details-artifacts-tabs"
      >
        <Box className="job-details-artifacts-tabs__loading">
          <ArrLoadingSpinner />
        </Box>
      </Box>
    );
  }

  if (!artifacts || tabData.length === 0) {
    return null;
  }

  return (
    <Box
      className="job-details-artifacts-tabs"
      data-testid="job-details-artifacts-tabs"
    >
      <Box className="job-details-artifacts-tabs__header">
        <Tabs
          value={activeTab}
          onChange={handleChange}
          className="job-details-artifacts-tabs__tabs"
        >
          {tabData.map((tab, index) => (
            <Tab
              key={tab.id}
              label={tab.label}
              className="job-details-artifacts-tabs__tab typo-text subhead4_size"
              data-testid={`job-details-artifacts-tab-${tab.id}`}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </Box>
      <Box
        className="job-details-artifacts-tabs__content"
        role="tabpanel"
        id={`job-details-artifacts-tabpanel-${activeTab}`}
        aria-labelledby={`job-details-artifacts-tab-${activeTab}`}
        data-testid={`job-details-artifacts-tabpanel-${tabData[activeTab]?.id}`}
      >
        {tabData[activeTab]?.content}
      </Box>
    </Box>
  );
};

export default JobDetailsArtifactsTabs;
